import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGet } from "../../api";
import { IKindredBrandsResponse } from "../../interfaces";
import { Button, Table, Spinner, Form, Row, Col, Badge } from "react-bootstrap";
import { Link } from "react-router";
import { Plus } from "react-feather";
import {
  listSearchKey,
  settingsType,
  useListSearchMemory,
  useSettings,
} from "../../hooks";

export const KindredBrands: React.FC = () => {
  const { hasPreviousSearch, initialPage, rememberSearch, forgetSearch } =
    useListSearchMemory(listSearchKey.KINDRED_BRANDS);
  const [page, setPage] = useState(initialPage);
  const [limit] = useState(100);

  // Filters (persisted)
  const [name, setName] = useSettings(settingsType.KINDRED_BRANDS_NAME);
  const [countryCode, setCountryCode] = useSettings(
    settingsType.KINDRED_BRANDS_COUNTRY
  );
  const [topBrands, setTopBrands] = useSettings(
    settingsType.KINDRED_BRANDS_TOP_BRANDS
  );
  const [domain, setDomain] = useSettings(settingsType.KINDRED_BRANDS_DOMAIN);

  // Reference data
  const refCountries = useGet<string[]>("kindred/reference/countries", true);

  // Build query
  let queryParams = `page=${page}&limit=${limit}`;
  if (name) queryParams += `&name=${encodeURIComponent(name)}`;
  if (countryCode) queryParams += `&countryCode=${countryCode}`;
  if (topBrands) queryParams += `&topBrands=true`;
  if (domain) queryParams += `&domain=${encodeURIComponent(domain)}`;

  const results = useGet<IKindredBrandsResponse>(
    `kindred/brands?${queryParams}`,
    false
  );

  const handleRefresh = useCallback(async () => {
    rememberSearch(page);
    await results.refetch();
  }, [results, rememberSearch, page]);

  // Re-run the last search on mount so returning from a detail page
  // restores the results, not just the filters.
  const { refetch } = results;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !hasPreviousSearch) return;
    restoredRef.current = true;
    refetch().catch(() => undefined);
  }, [hasPreviousSearch, refetch]);

  // Only a real page change re-runs the search — the mount render and the
  // reset done by Clear Filters already match what was fetched.
  const lastFetchedPageRef = useRef(initialPage);
  useEffect(() => {
    if (lastFetchedPageRef.current === page) return;
    lastFetchedPageRef.current = page;
    handleRefresh();
  }, [page]);

  const handleClearFilters = () => {
    setName("");
    setCountryCode("");
    setTopBrands(false);
    setDomain("");
    forgetSearch();
    lastFetchedPageRef.current = 1;
    setPage(1);
  };

  const pagination = results.data?.pagination;

  return (
    <div>
      <h4 className="mb-3">Kindred Live — Brands</h4>

      {/* Filters */}
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Form.Label className="small">Brand Name</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search brand name..."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Country</Form.Label>
          <Form.Select
            size="sm"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
          >
            <option value="">All</option>
            {(refCountries.data || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small">Domain</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search domains..."
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </Col>
        <Col md="auto">
          <Form.Label className="small d-block">&nbsp;</Form.Label>
          <Form.Check
            inline
            type="checkbox"
            label="Top Brands Only"
            checked={topBrands}
            onChange={(e) => setTopBrands(e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Button size="sm" variant="primary" onClick={handleRefresh}>
            {results.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Search"
            )}
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-primary"
            onClick={async () => {
              await handleRefresh();
            }}
          >
            Force Refresh
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
          <Link to="/kindred/brands/new" className="ms-2">
            <Button size="sm" variant="success">
              <Plus size={14} className="me-1" /> Create Brand
            </Button>
          </Link>
        </Col>
      </Row>

      {/* Results */}
      {results.error && (
        <div className="text-danger mb-3">Error: {results.error.message}</div>
      )}

      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Country</th>
            <th>Top Brand</th>
            <th>Domains</th>
          </tr>
        </thead>
        <tbody>
          {results.loading && (
            <tr>
              <td colSpan={5} className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading...
              </td>
            </tr>
          )}
          {!results.loading && results.data?.results.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-muted py-3">
                No results. Apply filters and click Search.
              </td>
            </tr>
          )}
          {(results.data?.results || []).map((brand) => (
            <tr key={brand.id}>
              <td>
                <Link to={`/kindred/brands/${brand.id}`}>
                  {brand.id.substring(0, 8)}...
                </Link>
              </td>
              <td>
                {brand.name}
                {brand.logo && (
                  <img
                    src={brand.logo}
                    alt=""
                    style={{
                      height: 20,
                      marginLeft: 8,
                      verticalAlign: "middle",
                    }}
                  />
                )}
              </td>
              <td>{brand.countryCode || "—"}</td>
              <td>
                {brand.isTopBrand ? <Badge bg="info">★ Top Brand</Badge> : "—"}
              </td>
              <td>{brand.domainCount}</td>
            </tr>
          ))}
        </tbody>
        {pagination && pagination.totalPages > 1 && (
          <tfoot>
            <tr>
              <td colSpan={5}>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small text-muted">
                    Showing page {pagination.page} of {pagination.totalPages} (
                    {pagination.total} total)
                  </span>
                  <div>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage(1)}
                    >
                      First
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>{" "}
                    <Form.Select
                      size="sm"
                      className="d-inline-block w-auto"
                      value={page}
                      onChange={(e) => setPage(Number(e.target.value))}
                    >
                      {Array.from({ length: pagination.totalPages }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </Form.Select>{" "}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page === pagination.totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(pagination.totalPages, p + 1))
                      }
                    >
                      Next
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page === pagination.totalPages}
                      onClick={() => setPage(pagination.totalPages)}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </Table>
    </div>
  );
};
